import { useState, useEffect } from 'react';
import {
  Save, Loader2, CheckCircle, AlertCircle, Plus, Trash2,
  Building2, MapPin, Share2, AlignLeft,
} from 'lucide-react';
import { adminApi, Footer as FooterType } from '../services/adminApi';

// chỉ các field kiểu string (bỏ addresses là string[])
type StrKey = Exclude<keyof FooterType, 'addresses'>;

const EMPTY: FooterType = {
  companyName: '', taxNumber: '', issueDate: '', issuer: '', representative: '',
  addresses: [], phone: '', email: '', websiteLabel: '', websiteUrl: '',
  facebookUrl: '', zaloUrl: '', youtubeUrl: '', hotline: '', copyright: '', description: '',
};

export default function FooterAdmin() {
  const [footer, setFooter] = useState<FooterType>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3500);
  };

  const load = async () => {
    try {
      const res = await adminApi.getFooter();
      if (res.success && res.data) setFooter(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k: StrKey, v: string) => setFooter((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.updateConfig({
        footer: { ...footer, addresses: footer.addresses.map((a) => a.trim()).filter(Boolean) },
      });
      if (res.success) showMessage('success', 'Đã lưu footer — vào Cài đặt → Công bố để hiển thị ngay');
      else showMessage('error', res.message || 'Lỗi khi lưu');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="center-screen">
        <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
      </div>
    );
  }

  const field = (label: string, key: StrKey, placeholder = '') => (
    <div className="field" style={{ flex: 1, margin: 0 }}>
      <label className="field-label">{label}</label>
      <input className="input" value={footer[key]} placeholder={placeholder} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  return (
    <div className="admin-container">
      {message && (
        <div className={`toast ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      <div className="page-head">
        <div>
          <h2 className="page-title">Quản lý Footer</h2>
          <p className="page-subtitle">Sửa toàn bộ nội dung chân trang — công ty, địa chỉ, liên hệ, mạng xã hội, hotline.</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={18} className="spin" /> : <Save size={18} />} Lưu
        </button>
      </div>

      <p className="muted" style={{ fontSize: '0.85rem', marginBottom: 16 }}>
        Footer dùng chung phiên bản dữ liệu với chi phí / ngành hàng. Sửa xong bấm <b>Lưu</b>, rồi vào{' '}
        <b>Cài đặt → Công bố</b> để người dùng nhận ngay (không chờ cache hết hạn).
      </p>

      <div className="stack" style={{ gap: 20 }}>
        {/* Thông tin công ty */}
        <div className="card">
          <div className="card-head"><h3><Building2 size={18} style={{ color: '#2563eb' }} /> Thông tin công ty</h3></div>
          <div className="card-pad stack" style={{ gap: 12 }}>
            {field('Tên công ty', 'companyName')}
            <div className="row" style={{ gap: 12 }}>
              {field('Mã số thuế', 'taxNumber')}
              {field('Ngày cấp', 'issueDate')}
            </div>
            {field('Nơi cấp', 'issuer')}
            {field('Người đại diện', 'representative')}
          </div>
        </div>

        {/* Địa chỉ & liên hệ */}
        <div className="card">
          <div className="card-head"><h3><MapPin size={18} style={{ color: '#2563eb' }} /> Địa chỉ & liên hệ</h3></div>
          <div className="card-pad stack" style={{ gap: 12 }}>
            <div className="field" style={{ margin: 0 }}>
              <label className="field-label">Địa chỉ (mỗi dòng 1 địa chỉ)</label>
              <div className="stack" style={{ gap: 8 }}>
                {footer.addresses.map((addr, i) => (
                  <div className="row" key={i}>
                    <input
                      className="input"
                      value={addr}
                      placeholder="VD: Địa chỉ tại Hà Nội: Số 35 Lê Văn Lương..."
                      onChange={(e) => setFooter((s) => ({ ...s, addresses: s.addresses.map((a, idx) => (idx === i ? e.target.value : a)) }))}
                    />
                    <button className="btn-icon danger" title="Xoá" onClick={() => setFooter((s) => ({ ...s, addresses: s.addresses.filter((_, idx) => idx !== i) }))}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {footer.addresses.length === 0 && <p className="muted" style={{ fontStyle: 'italic' }}>Chưa có địa chỉ nào.</p>}
                <button className="add-link" style={{ alignSelf: 'flex-start' }} onClick={() => setFooter((s) => ({ ...s, addresses: [...s.addresses, ''] }))}>
                  <Plus size={16} /> Thêm địa chỉ
                </button>
              </div>
            </div>
            <div className="row" style={{ gap: 12 }}>
              {field('Điện thoại', 'phone')}
              {field('Email', 'email')}
            </div>
            <div className="row" style={{ gap: 12 }}>
              {field('Chữ hiển thị website', 'websiteLabel', 'https://...')}
              {field('Link website', 'websiteUrl', 'https://...')}
            </div>
          </div>
        </div>

        {/* Mạng xã hội & hotline */}
        <div className="card">
          <div className="card-head"><h3><Share2 size={18} style={{ color: '#2563eb' }} /> Mạng xã hội & hotline</h3></div>
          <div className="card-pad stack" style={{ gap: 12 }}>
            {field('Facebook (link icon)', 'facebookUrl', 'https://facebook.com/...')}
            {field('Zalo (link icon)', 'zaloUrl', 'https://zalo.me/...')}
            {field('YouTube (link icon)', 'youtubeUrl', 'https://youtube.com/...')}
            {field('Hotline (nút gọi nổi)', 'hotline', 'VD: 0834713331')}
          </div>
        </div>

        {/* Dòng cuối trang */}
        <div className="card">
          <div className="card-head"><h3><AlignLeft size={18} style={{ color: '#2563eb' }} /> Dòng cuối trang</h3></div>
          <div className="card-pad stack" style={{ gap: 12 }}>
            {field('Bản quyền', 'copyright')}
            <div className="field" style={{ margin: 0 }}>
              <label className="field-label">Mô tả</label>
              <textarea className="input" rows={2} value={footer.description} onChange={(e) => set('description', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
